import React, {
  useCallback, useMemo, Fragment, ReactElement,
} from 'react';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import TableRowForm from '../../../../components/table-row-form/table-row-form.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import FilteredCell from '../../../../components/filtered-cell/filtered-cell.component';
import IconBlockError from '../../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../../components/icon-block-success/icon-block-success.component';
import Loader from '../../../../components/loader/loader.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../../components/form-field-number/form-field-number.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import RemoveIcon from '../../../../icons/remove-icon';
import ArrowLinkIcon from '../../../../icons/arrow-link-icon';
import FormatService from '../../../../services/format-service';
import FormService from '../../../../services/form-service';
import useShiftState from '../../../../hooks/use-shift-state';
import { TABLE_NAME_DEAL_CAPITALIZATION } from '../../../../constants/system/table-names';

const DealCapitalizationRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const FIELD_NAME_AT_CLOSING_AMOUNT: string = 'atClosingAmount';
  const FIELD_NAME_FUTURE_AMOUNT: string = 'futureAmount';
  const FIELD_NAME_AMOUNT: string = 'amount';

  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isNewEntity,
    isSimpleView,
    dealCapitalizationItemData,
    dealCapitalizationGroupData,
    dealCapitalizationCategory,
    dealCapitalizationItemDynamicData,
    dealCapitalizationNewItems,
    dealCapitalizationItems,
    dealCapitalizationItemIndex,
    inputRefs,
    currentUser,
    deleteItemPermissions,
    dealCapitalizationTypeList,
    dealCapitalizationGroupList,
    onInlineSave,
    onInlineCancel,
    onOpenModal,
    onDeleteClick,
    onItemChange,
  } = props;

  const isShiftPressed: boolean = useShiftState();

  const dealCapitalizationGroup: DealCapitalizationGroup = useMemo((): DealCapitalizationGroup => (
    dealCapitalizationGroupData && dealCapitalizationGroupList
    && dealCapitalizationGroupList.find((dealCapitalizationGroupItem: DealCapitalizationGroup): boolean => (
      dealCapitalizationGroupItem.title === dealCapitalizationGroupData.name.trim()
    ))
  ), [dealCapitalizationGroupData, dealCapitalizationGroupList]);
  const filteredDealCapitalizationTypeList: DealCapitalizationType[] = useMemo((): DealCapitalizationType[] => (
    dealCapitalizationGroup && dealCapitalizationTypeList
    && dealCapitalizationTypeList.filter((dealCapitalizationTypeItem: DealCapitalizationType): boolean => (
      dealCapitalizationTypeItem.group === dealCapitalizationGroup.name
    ))
  ), [dealCapitalizationGroup, dealCapitalizationTypeList]);

  const dealCapitalizationItemId: number = (dealCapitalizationItemData && dealCapitalizationItemData.id)
    || FormService.FORM_KEY_NEW;
  const dealCapitalizationGroupId: number = (dealCapitalizationGroupData && dealCapitalizationGroupData.id)
    || FormService.FORM_KEY_NEW;
  const formKeyDealCapitalizationItem: string = FormService.getIndexedFormFieldKey(dealCapitalizationItemId);
  const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(dealCapitalizationGroupId);
  const formPrefix: string = `dealData.dealCapitalizationData.${dealCapitalizationCategory}`
    + `[${formKeyDealCapitalizationGroup}].items[${formKeyDealCapitalizationItem}]`;

  const menuItemFilteredDealCapitalizationTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: filteredDealCapitalizationTypeList,
  }), [filteredDealCapitalizationTypeList]);

  const deleteClick: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (onDeleteClick) {
      onDeleteClick(event);
    }
  }, [dealCapitalizationNewItems, dealCapitalizationItems]);

  const itemChange: Function = useCallback((
    fieldName: string, value: number, isNumber?: boolean, isFloat?: boolean,
  ): void => {
    if (onItemChange) {
      onItemChange(dealCapitalizationItemData.id, dealCapitalizationGroupData.id, fieldName, value, isNumber, isFloat);
    }
  }, [dealCapitalizationItemData, dealCapitalizationGroupData, dealCapitalizationNewItems, dealCapitalizationItems]);

  const tableDealCapitalizationItemListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.name`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealCapitalizationItemIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'type.title',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = filteredDealCapitalizationTypeList && !filteredDealCapitalizationTypeList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.type`}
              optionsTotal={filteredDealCapitalizationTypeList && filteredDealCapitalizationTypeList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealCapitalizationItemIndex}.${fieldIndex}` : null}
            >
              {menuItemFilteredDealCapitalizationTypeList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 3,
      name: 'atClosingAmount',
      isHidden: isSimpleView,
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.atClosingAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealCapitalizationItemIndex}.${fieldIndex}`}
            onChange={(value: number): void => {
              itemChange(FIELD_NAME_AT_CLOSING_AMOUNT, value, true);
            }}
          />
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'futureAmount',
      isHidden: isSimpleView,
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.futureAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealCapitalizationItemIndex}.${fieldIndex}`}
            onChange={(value: number): void => {
              itemChange(FIELD_NAME_FUTURE_AMOUNT, value, true);
            }}
          />
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'amount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.amount`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealCapitalizationItemIndex}.${fieldIndex}`}
            onChange={(value: number): void => {
              itemChange(FIELD_NAME_AMOUNT, value, true);
            }}
          />
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'gsfAmount',
      isHidden: isSimpleView,
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealCapitalizationItemData.gsfAmount || '-'}
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'nsfAmount',
      isHidden: isSimpleView,
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealCapitalizationItemData.nsfAmount || '-'}
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'zfaAmount',
      isHidden: isSimpleView,
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealCapitalizationItemData.zfaAmount || '-'}
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'unitsAmount',
      isHidden: isSimpleView,
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealCapitalizationItemData.unitsAmount || '-'}
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'keysAmount',
      isHidden: isSimpleView,
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealCapitalizationItemData.keysAmount || '-'}
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'acresAmount',
      isHidden: isSimpleView,
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealCapitalizationItemData.acresAmount || '-'}
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'lotsfAmount',
      isHidden: isSimpleView,
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealCapitalizationItemData.lotsfAmount || '-'}
        </TableCell>
      ),
    },
    {
      id: 13,
      name: 'sharePercent',
      component: (): ReactElement => (
        <TableCell>
          {dealCapitalizationItemDynamicData && dealCapitalizationItemDynamicData.sharePercent
            && FormatService.formatNumber((dealCapitalizationItemDynamicData.sharePercent)
              .toFixed(FormatService.FLOAT_NUMBER_PRECISION_DEFAULT), true) || ''}
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'notes',
      isHidden: isSimpleView,
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          {
            !isSimpleView && (
              <FormFieldText
                isInline
                isMultiline
                name={`${formPrefix}.notes`}
                maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                inputRefs={inputRefs}
                inputRefIndex={`${dealCapitalizationItemIndex}.${fieldIndex}`}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 15,
      name: 'actions',
      isNotFiltered: true,
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            !isNewEntity && !isSimpleView && isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isNewEntity && !isSimpleView && !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(dealCapitalizationGroupData.id, dealCapitalizationItemData.id);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isNewEntity && !isSimpleView && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(dealCapitalizationGroupData.id, dealCapitalizationItemData.id);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
          {
            !isSaving && (
              <CheckAccess permissions={deleteItemPermissions} isEnabled={!isNewEntity}>
                <Tooltip title="Delete">
                  <ActionButton isInline onClick={deleteClick}>
                    <RemoveIcon />
                  </ActionButton>
                </Tooltip>
              </CheckAccess>
            )
          }
          {
            !isSimpleView && !isSaving && !isActive && !isNewEntity ? (
              <Tooltip title="Open in modal">
                <ActionButton isInline withNoPadding onClick={onOpenModal}>
                  <ArrowLinkIcon />
                </ActionButton>
              </Tooltip>
            ) : null
          }
        </TableCell>
      ),
    },
  ], [
    isSimpleView, isNewEntity, dealCapitalizationItemDynamicData, isSaving, isActive, isShiftPressed,
    filteredDealCapitalizationTypeList, dealCapitalizationNewItems, dealCapitalizationItems, isSaveDisabled,
  ]);

  return (
    <TableRowForm isActive={isActive}>
      {
        tableDealCapitalizationItemListFields && !!tableDealCapitalizationItemListFields.length
        && tableDealCapitalizationItemListFields.map((
          tableField: TableField, fieldIndex: number,
        ): ReactElement | boolean => (
          !!tableField.component && !tableField.isHidden && (
            !tableField.isNotFiltered && !isSimpleView ? (
              <FilteredCell
                key={tableField.id}
                tableName={TABLE_NAME_DEAL_CAPITALIZATION}
                columnName={tableField.name}
                currentUser={currentUser}
              >
                {tableField.component(tableField, fieldIndex)}
              </FilteredCell>
            ) : (
              <Fragment key={tableField.id}>
                {tableField.component(tableField, fieldIndex)}
              </Fragment>
            )
          )
        ))
      }
    </TableRowForm>
  );
});

export default DealCapitalizationRowForm;
